<template>
  <div class="profile-page">
    <div class="page-header">
      <h1><i class="pi pi-user"></i> Мій профіль</h1>
      <p>Управління особистими даними та налаштуваннями</p>
    </div>

    <div class="profile-content">
      <div class="profile-grid">
        <div class="profile-card">
          <div class="card-header">
            <h2>Особиста інформація</h2>
            <Button
              icon="pi pi-pencil"
              class="p-button-text"
              @click="editMode = !editMode"
              v-tooltip.top="editMode ? 'Скасувати редагування' : 'Редагувати'"
            />
          </div>

          <div class="profile-info">
            <div class="avatar-section">
              <div class="avatar">
                <i class="pi pi-user"></i>
              </div>
              <div class="user-details">
                <h3>{{ currentUser?.first_name }} {{ currentUser?.last_name }}</h3>
                <p class="username">@{{ currentUser?.username }}</p>
                <Tag :value="getRoleLabel(currentUser?.role)" :severity="currentUser?.role === 'admin' ? 'danger' : 'info'" />
              </div>
            </div>

            <div class="info-form" v-if="editMode">
              <div class="form-row">
                <div class="form-group">
                  <label>Ім'я:</label>
                  <InputText v-model="editForm.first_name" />
                </div>
                <div class="form-group">
                  <label>Прізвище:</label>
                  <InputText v-model="editForm.last_name" />
                </div>
              </div>
              
              <div class="form-group">
                <label>Email:</label>
                <InputText v-model="editForm.email" type="email" />
              </div>
              
              <div class="form-row">
                <div class="form-group">
                  <label>Відділ:</label>
                  <InputText v-model="editForm.department" />
                </div>
                <div class="form-group">
                  <label>Посада:</label>
                  <InputText v-model="editForm.position" />
                </div>
              </div>
              
              <div class="form-group">
                <label>ID співробітника:</label>
                <InputText v-model="editForm.employee_id" />
              </div>

              <div class="form-actions">
                <Button label="Зберегти" @click="saveProfile" class="p-button-success" />
                <Button label="Скасувати" @click="cancelEdit" class="p-button-text" />
              </div>
            </div>

            <div class="info-display" v-else>
              <div class="info-item">
                <span class="info-label">Email:</span>
                <span class="info-value">{{ currentUser?.email }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Відділ:</span>
                <span class="info-value">{{ currentUser?.department || 'Не вказано' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Посада:</span>
                <span class="info-value">{{ currentUser?.position || 'Не вказано' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">ID співробітника:</span>
                <span class="info-value">{{ currentUser?.employee_id || 'Не вказано' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Дата реєстрації:</span>
                <span class="info-value">{{ formatDate(currentUser?.date_joined) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="profile-card">
          <div class="card-header">
            <h2>Безпека</h2>
          </div>

          <div class="security-section">
            <div class="security-item">
              <div class="security-info">
                <h4>Зміна пароля</h4>
                <p>Оновіть свій пароль для підвищення безпеки</p>
              </div>
              <Button
                label="Змінити пароль"
                icon="pi pi-key"
                @click="showChangePasswordDialog = true"
              />
            </div>

            <div class="security-item">
              <div class="security-info">
                <h4>Двофакторна автентифікація</h4>
                <p>Додатковий рівень захисту вашого акаунту</p>
              </div>
              <div class="toggle-section">
                <span class="toggle-label">Увімкнено</span>
                <InputSwitch v-model="twoFactorEnabled" />
              </div>
            </div>

            <div class="security-item">
              <div class="security-info">
                <h4>Сесії</h4>
                <p>Керування активними сесіями</p>
              </div>
              <Button
                label="Переглянути сесії"
                icon="pi pi-desktop"
                class="p-button-secondary"
                @click="viewSessions"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="profile-card">
        <div class="card-header">
          <h2>Моя активність</h2>
        </div>

        <div class="activity-section">
          <div class="activity-stats">
            <div class="stat-item">
              <div class="stat-icon">
                <i class="pi pi-file"></i>
              </div>
              <div class="stat-content">
                <h3>{{ completedTestsCount }}</h3>
                <p>Завершених тестів</p>
              </div>
            </div>
            
            <div class="stat-item">
              <div class="stat-icon">
                <i class="pi pi-calendar"></i>
              </div>
              <div class="stat-content">
                <h3>{{ activeSessionsCount }}</h3>
                <p>Активних сесій</p>
              </div>
            </div>
            
            <div class="stat-item">
              <div class="stat-icon">
                <i class="pi pi-star"></i>
              </div>
              <div class="stat-content">
                <h3>{{ averageScore }}</h3>
                <p>Середній бал</p>
              </div>
            </div>
          </div>

          <div class="recent-activity">
            <h3>Остання активність</h3>
            <div class="activity-list">
              <div v-for="activity in recentActivities" :key="activity.id" class="activity-item">
                <div class="activity-icon">
                  <i :class="activity.icon"></i>
                </div>
                <div class="activity-content">
                  <div class="activity-text">{{ activity.text }}</div>
                  <div class="activity-time">{{ formatTime(activity.time) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Change Password Dialog -->
    <Dialog
      v-model:visible="showChangePasswordDialog"
      header="Зміна пароля"
      :modal="true"
      class="p-dialog"
    >
      <div class="password-form">
        <div class="form-group">
          <label>Поточний пароль:</label>
          <Password v-model="passwordForm.current" :feedback="false" />
        </div>
        <div class="form-group">
          <label>Новий пароль:</label>
          <Password v-model="passwordForm.new" />
        </div>
        <div class="form-group">
          <label>Підтвердження нового пароля:</label>
          <Password v-model="passwordForm.confirm" :feedback="false" />
        </div>
      </div>
      <template #footer>
        <Button label="Скасувати" @click="showChangePasswordDialog = false" class="p-button-text" />
        <Button label="Змінити пароль" @click="changePassword" class="p-button-success" />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'Profile',
  data() {
    return {
      editMode: false,
      showChangePasswordDialog: false,
      twoFactorEnabled: false,
      editForm: {
        first_name: '',
        last_name: '',
        email: '',
        department: '',
        position: '',
        employee_id: ''
      },
      passwordForm: {
        current: '',
        new: '',
        confirm: ''
      }
    }
  },
  computed: {
    ...mapState(['currentUser', 'results', 'sessions', 'recentActivities']),
    ...mapGetters(['isAdmin']),
    completedTestsCount() {
      return this.results.filter(result => result.user === this.currentUser?.id).length
    },
    activeSessionsCount() {
      return this.sessions.filter(session => 
        session.status === 'active' && 
        session.participants?.some(p => p.user === this.currentUser?.id)
      ).length
    },
    averageScore() {
      const userResults = this.results.filter(result => result.user === this.currentUser?.id)
      if (userResults.length === 0) return 'N/A'
      const total = userResults.reduce((sum, result) => sum + (result.average_score || 0), 0)
      return (total / userResults.length).toFixed(2)
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'Не вказано'
      return new Date(dateString).toLocaleDateString('uk-UA')
    },
    formatTime(date) {
      return new Date(date).toLocaleString('uk-UA')
    },
    getRoleLabel(role) {
      const labels = {
        admin: 'Адміністратор',
        user: 'Користувач'
      }
      return labels[role] || role
    },
    async saveProfile() {
      try {
        await this.$store.dispatch('updateProfile', this.editForm)
        this.$toast.add({
          severity: 'success',
          summary: 'Успіх',
          detail: 'Профіль оновлено',
          life: 3000
        })
        this.editMode = false
      } catch (error) {
        const message = error.response?.data?.detail || 'Не вдалося оновити профіль'
        this.$toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: message,
          life: 3000
        })
      }
    },
    cancelEdit() {
      this.editForm = {
        first_name: this.currentUser?.first_name || '',
        last_name: this.currentUser?.last_name || '',
        email: this.currentUser?.email || '',
        department: this.currentUser?.department || '',
        position: this.currentUser?.position || '',
        employee_id: this.currentUser?.employee_id || ''
      }
      this.editMode = false
    },
    async changePassword() {
      if (this.passwordForm.new !== this.passwordForm.confirm) {
        this.$toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: 'Паролі не співпадають',
          life: 3000
        })
        return
      }
      try {
        await this.$store.dispatch('changePassword', {
          old_password: this.passwordForm.current,
          new_password: this.passwordForm.new,
          new_password_confirm: this.passwordForm.confirm
        })
        this.$toast.add({
          severity: 'success',
          summary: 'Успіх',
          detail: 'Пароль змінено',
          life: 3000
        })
        this.showChangePasswordDialog = false
        this.passwordForm = { current: '', new: '', confirm: '' }
      } catch (error) {
        const message = error.response?.data?.detail || 'Не вдалося змінити пароль'
        this.$toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: message,
          life: 3000
        })
      }
    },
    async loadRecentActivities() {
      try {
        // Перевіряємо, чи користувач авторизований та є адміністратором
        if (this.currentUser && this.currentUser.is_admin) {
          await this.$store.dispatch('fetchRecentActivities')
        }
      } catch (error) {
        // Не показуємо помилку користувачу, якщо це не адмін
      }
    },
    viewSessions() {
      // Navigate to sessions view or show sessions dialog
    }
  },
  mounted() {
    // Initialize edit form with current user data
    this.editForm = {
      first_name: this.currentUser?.first_name || '',
      last_name: this.currentUser?.last_name || '',
      email: this.currentUser?.email || '',
      department: this.currentUser?.department || '',
      position: this.currentUser?.position || '',
      employee_id: this.currentUser?.employee_id || ''
    }
    this.loadRecentActivities()
  }
}
</script>

<style scoped>
.profile-page {
  padding: 2rem;
}

.page-header {
  margin-bottom: 2rem;
}

.page-header h1 {
  color: var(--primary-color);
  margin-bottom: 0.5rem;
}

.page-header p {
  color: var(--text-color-secondary);
  margin: 0;
}

.profile-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.profile-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
}

.profile-card {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius);
  padding: 2rem;
  box-shadow: var(--shadow);
  color: var(--text-color);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.card-header h2 {
  margin: 0;
  color: var(--text-color);
}

.profile-info {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.avatar-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2rem;
}

.user-details h3 {
  margin: 0 0 0.25rem 0;
  color: var(--text-color);
}

.username {
  margin: 0 0 0.5rem 0;
  color: var(--text-color-secondary);
}

.info-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: var(--text-color);
}

.form-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.info-display {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid var(--surface-border);
  border-radius: 6px;
}

.info-label {
  font-weight: 600;
  color: var(--text-color);
}

.info-value {
  color: var(--text-color-secondary);
}

.security-section {
  margin-top: 1.5rem;
}

.security-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  margin-bottom: 1rem;
}

.security-info h4 {
  margin: 0 0 0.25rem 0;
  color: var(--text-color);
}

.security-info p {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
}

.activity-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-item {
  text-align: center;
  padding: 1rem;
  border: 1px solid var(--surface-border);
  border-radius: 6px;
}

.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.stat-content h3 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--text-color);
}

.stat-content p {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
}

.recent-activity h3 {
  margin: 0 0 1rem 0;
  color: var(--text-color);
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.activity-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--surface-border);
  border-radius: 6px;
}

.activity-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.875rem;
}

.activity-content {
  flex: 1;
}

.activity-text {
  color: var(--text-color);
  margin-bottom: 0.25rem;
}

.activity-time {
  color: var(--text-color-secondary);
  font-size: 0.875rem;
}

.password-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

@media (max-width: 768px) {
  .profile-page {
    padding: 1rem;
  }
  
  .profile-grid {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .security-item {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .activity-stats {
    grid-template-columns: 1fr;
  }
  
}
</style> 